// UI primitives
import { UNDERLINE_STATIC } from '@/components/ui/link-underline';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

// Icons
import { AlertIcon } from '../icons';
import { RefreshIcon } from '@icons/RefreshIcon';

// Hooks and utilities
import { useIsFetching, useQueryClient } from '@tanstack/react-query';
import { useRecomputeOrderTotals } from '@hooks/orders';
import { useTranslation } from '@hooks/useTranslation';
import { formatShortDate, formatTime, formatCurrency } from '@/react/utils/formatters';

// Event tags
import { drawableEventTags } from '@/react/constants/eventTags';

// Diet/chef indicators
import { DietTagIcons } from '../Product/DietTagIcons';
import { NameWithDietIcons } from '../Product/NameWithDietIcons';
import { EventBreakdownSummary } from './EventBreakdownSummary';
import { MenuItemNotes, toNoteLines } from './MenuItemNotes';

// Types
import { eventsQueryKeys, type ApiEvent, type ApiEventLineItem, type ApiSpineOrderLineItem } from '@api/events';
import { PRODUCT_LINE_LABELS } from '@components/common/ProductLinePicker/productLines';
import { deriveEventChips } from '@components/common/eventChips';
import { EVENT_CHIP_CLASS, tagChipColours } from '@components/common/eventChipStyles';
import { formatEventAddress } from './event-address';
import { isPlaceholderPostalCode } from '../../utils/postalCode';

import { INTERPUNCT, ALERT_PILL_CLASS, CARD_BODY_CLASS, CARD_RADIUS, CARD_TITLE_CLASS, toSentenceCase } from './event-card-shared';

const ROW_CLASS = 'grid grid-cols-1 gap-6 lg:grid-cols-[1fr_1fr]';

/**
 * Presentational field renderers for an event's expanded detail — shared by
 * the desktop inline card and the mobile bottom-sheet accordion (both live in
 * EventsSectionCard.tsx / EventDetailMobileCard.tsx).
 */

export function EventTypeMeta({ event, tagsSingleLine = false }: { event: ApiEvent; tagsSingleLine?: boolean }) {
  const { t, getSection } = useTranslation();
  const eventTypes = getSection('events.event_types') as Record<string, string> | undefined;
  const units = getSection('events.units') as Record<string, string> | undefined;

  // Row 2 lead: format type (event_type) · guests · lines · ways.
  // Product line + status moved to the header (row 1); occasion dropped per the
  // ticket's row spec (tags carry that grouping instead).
  const guestsLabel = units?.guests ?? 'guests';
  const linesLabel = units?.lines ?? 'lines';
  const waysLabel = units?.ways ?? 'ways';

  const metaParts: string[] = [];
  const formatType = eventTypes?.[event.event_type] ?? (event.event_type ? toSentenceCase(event.event_type) : null);
  if (formatType) metaParts.push(formatType);
  if (event.pax_count != null) metaParts.push(`${event.pax_count} ${guestsLabel}`);
  if (event.line_count != null) metaParts.push(`${event.line_count} ${linesLabel}`);
  if (event.way_count != null) metaParts.push(`${event.way_count} ${waysLabel}`);

  // Event tags — Papercut-aligned occasion tokens from Core metadata.tags.
  // Internal operational flags are dropped so a raw token never shows.
  const tags = drawableEventTags(event.tags ?? []);

  // Requirement/date chips — shared derivation with the events list (GRAIN-2814).
  const eventChips = deriveEventChips(event);

  if (metaParts.length === 0 && tags.length === 0 && eventChips.length === 0) return null;

  const metaLine = metaParts.length > 0 ? (
    <p className="font-inter text-sm font-normal leading-normal text-off-black">
      {metaParts.join(INTERPUNCT)}
    </p>
  ) : null;
  const chips = [
    ...tags.map((tag, index) => (
      <MetaChip key={`tag-${tag}-${index}`} colours={tagChipColours(tag)}>{t(`event_tags.${tag}`, tag)}</MetaChip>
    )),
    ...eventChips.map((chip) => (
      <MetaChip key={chip.key} colours={chip.colours}>{t(chip.labelKey, chip.labelParams)}</MetaChip>
    )),
  ];

  return (
    <div className="flex flex-col gap-3">
      {tagsSingleLine ? (
        // Mobile: meta on one line, tag/requirement chips on a single
        // horizontally-scrollable row (no wrap) so they never stack.
        <div className="flex flex-col gap-2">
          {metaLine}
          {chips.length > 0 && (
            <div className="flex items-center gap-2 overflow-x-auto [&>*]:shrink-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {chips}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-wrap items-center gap-4">
          {metaLine}
          {chips}
        </div>
      )}
      <Separator className="bg-grey-light" />
    </div>
  );
}

function MetaChip({ children, colours }: { children: React.ReactNode; colours: string }) {
  return <span className={cn(EVENT_CHIP_CLASS, colours)}>{children}</span>;
}

export function EventServingAndTimings({ event }: { event: ApiEvent }) {
  const { t } = useTranslation();
  const timings = event.timing_details;
  const servingTime = timings?.serving_time ?? event.serving_time;
  const consumptionWindow = event.scheduled_at ? (
    <>
      {timings?.ready_to_eat_at ? (
        <Value>{t('events_section.ready_to_eat_at')}: {formatTime(timings.ready_to_eat_at)}</Value>
      ) : null}
      {timings?.consume_by ? (
        <Value>{t('events_section.consume_by')}: {formatTime(timings.consume_by)}</Value>
      ) : null}
    </>
  ) : null;

  return (
    <>
      <div className={ROW_CLASS}>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[max-content_minmax(0,1fr)] md:gap-x-12">
          <div className="flex flex-col gap-1.5 min-w-0">
            <Label>{t('events_section.serving_date')}</Label>
            {event.scheduled_at ? (
              <div className="flex flex-wrap items-center gap-x-6 gap-y-1.5">
                <Value>{formatShortDate(event.scheduled_at)}</Value>
                {servingTime ? <Value className="md:hidden">{formatTime(servingTime)}</Value> : null}
              </div>
            ) : (
              <EmptyValue />
            )}
          </div>

          <div className="grid grid-cols-3 gap-6 md:flex md:flex-nowrap md:items-start md:gap-x-10">
            <TimingColumn className="hidden md:flex" label={t('events_section.timing_serving')} value={servingTime} />
            <TimingColumn label={t('events_section.timing_pickup')} value={timings?.pickup_time ?? event.pickup_time} />
            <TimingColumn label={t('events_section.timing_setup')} value={timings?.setup_time} />
            <TimingColumn label={t('events_section.timing_teardown')} value={timings?.teardown_time} />
          </div>
        </div>

        <div className="flex flex-wrap items-end gap-x-3 gap-y-2">
          {consumptionWindow}
        </div>
      </div>
      <Separator className="bg-grey-light" />
    </>
  );
}

export function EventAddressAndContacts({ event }: { event: ApiEvent }) {
  const { t } = useTranslation();
  const host = event.host_info;
  const addressText = formatEventAddress(event, t);

  const primaryParts = [
    host?.primary_host_name ?? event.contact_name,
    host?.primary_host_number ?? event.contact_phone,
  ].filter(Boolean);
  const secondaryParts = [host?.secondary_host_name, host?.secondary_host_number].filter(Boolean);

  return (
    <>
      <div className={ROW_CLASS}>
        <div className="flex flex-col gap-1.5 min-w-0">
          <Label>{t('events_section.event_address')}</Label>
          {addressText ? (
            <Value className={cn(isPlaceholderPostalCode(event.postal_code) && 'text-chilli-red')}>
              {addressText}
            </Value>
          ) : (
            <EmptyValue />
          )}
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
          <div>
            <Label>{t('events_section.primary_contact')}</Label>
            {primaryParts.length > 0 ? (
              <Value>{primaryParts.join(INTERPUNCT)}</Value>
            ) : (
              <EmptyValue />
            )}
          </div>
          <div>
            <Label>{t('events_section.secondary_contact')}</Label>
            {secondaryParts.length > 0 ? (
              <Value>{secondaryParts.join(INTERPUNCT)}</Value>
            ) : (
              <EmptyValue />
            )}
          </div>
        </div>
      </div>
      <Separator className="bg-grey-light" />
    </>
  );
}

export function EventDescriptionAndInstructions({ event }: { event: ApiEvent }) {
  const { t } = useTranslation();
  const description = event.metadata?.description;
  const instructions = event.metadata?.special_instructions;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
      <div className="min-w-0">
        <Label>{t('events_section.event_description')}</Label>
        {description ? (
          <div className="mt-1.5 font-inter text-sm font-normal leading-normal text-off-black whitespace-pre-line">
            {description}
          </div>
        ) : (
          <EmptyValue />
        )}
      </div>
      <div className="min-w-0">
        <Label>{t('events_section.special_instructions')}</Label>
        {instructions ? (
          <div className="mt-1.5 font-inter text-sm font-normal leading-normal text-off-black whitespace-pre-line">
            {instructions}
          </div>
        ) : (
          <EmptyValue />
        )}
      </div>
    </div>
  );
}

// TODO: Replace with real yield data from backend once available
export function EventYieldBar() {
  const { t } = useTranslation();

  return (
    <div className={cn('flex flex-col gap-2 border border-dinnerplate-white bg-yield-bar', CARD_RADIUS, 'p-6')}>
      <div className="flex items-center gap-4">
        <span className={CARD_TITLE_CLASS}>
          {t('events_section.event_yield')}
        </span>
        <Badge variant="outline" className="tabular-nums rounded-xs border-yield-positive-border bg-yield-positive-bg px-2 py-1 font-inter text-xs font-semibold leading-tight text-yield-positive">
          0.00%
        </Badge>
      </div>
      {/* `whitespace-nowrap` per figure so a label never parts from its amount. */}
      {/* TODO: hardcoded zeros — replace with real values once event financial calc is wired. */}
      <div className="flex flex-wrap gap-x-8 gap-y-2 tabular-nums">
        <span className={cn(CARD_BODY_CLASS, 'whitespace-nowrap text-off-black')}>
          {t('events_section.revenue')}: {formatCurrency(0)}
        </span>
        <span className={cn(CARD_BODY_CLASS, 'whitespace-nowrap text-off-black')}>
          {t('events_section.food_cost')}: {formatCurrency(0)}
        </span>
        <span className={cn(CARD_BODY_CLASS, 'whitespace-nowrap text-off-black')}>
          {t('events_section.ops_manpower')}: {formatCurrency(0)}
        </span>
      </div>
    </div>
  );
}

// Linked Spine (meal-box) order card, rendered beside the Event yield bar.
// The identifier links out to Spine admin's order search; Refresh re-persists
// the order's stored totals snapshot, then refetches the drawer's events
// query so the Spine details are re-read live.
export function SpineOrderCard({ event }: { event: ApiEvent }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const recomputeTotals = useRecomputeOrderTotals();
  const orderId = event.order_id;
  const isRefreshing =
    useIsFetching({ queryKey: orderId != null ? eventsQueryKeys.orderEvents(orderId) : eventsQueryKeys.all }) > 0;
  const spineOrder = event.spine_order;
  if (!spineOrder) return null;

  // Degraded after linking: cancelled in Spine, or gone from Spine (the
  // serializer emits a bare {identifier} node when the order vanished).
  const cancelled = spineOrder.state === 'cancelled';
  const deleted = spineOrder.state == null;
  const degraded = cancelled || deleted;

  // Recompute first so the stored Order value snapshot tracks the Spine-side
  // change, then refetch the drawer regardless of the write's outcome (the
  // read-only refresh is still worth doing when the recompute fails).
  const handleRefresh = () => {
    if (orderId == null) return;
    recomputeTotals.mutate(orderId, {
      onSettled: () => {
        queryClient.invalidateQueries({ queryKey: eventsQueryKeys.orderEvents(orderId) });
      },
    });
  };

  return (
    // The degraded notice sits inside the card, so the card is the grid child
    // and stretches to the row height itself.
    <div className={cn('flex flex-col justify-center gap-2 border border-dinnerplate-white bg-dinnerplate-white-lighter', CARD_RADIUS, 'px-6 py-3')}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <span className={CARD_TITLE_CLASS}>
            {t('events_section.spine_order_id')}
          </span>
          {spineOrder.admin_url ? (
            <a
              href={spineOrder.admin_url}
              target="_blank"
              rel="noopener noreferrer"
              className={cn(
                CARD_BODY_CLASS,
                UNDERLINE_STATIC,
                degraded ? 'text-chilli-red hover:text-chilli-red' : 'text-grey hover:text-off-black',
              )}
            >
              {spineOrder.identifier}
            </a>
          ) : (
            <span className={cn(CARD_BODY_CLASS, degraded ? 'text-chilli-red' : 'text-grey')}>
              {spineOrder.identifier}
            </span>
          )}
        </div>
        <Button
          variant="tertiary-compact"
          className="gap-1.5 pr-3 text-[15px]"
          onClick={handleRefresh}
          disabled={orderId == null || isRefreshing || recomputeTotals.isPending}
        >
          <RefreshIcon className="size-5" aria-hidden="true" />
          {t('events_section.spine_order_refresh')}
        </Button>
      </div>
      {degraded && (
        <div className={ALERT_PILL_CLASS} data-testid="spine-order-degraded-notice">
          <AlertIcon aria-hidden="true" className="size-4 shrink-0" />
          <span>
            {t(cancelled ? 'events_section.spine_order_cancelled_notice' : 'events_section.spine_order_deleted_notice')}
          </span>
        </div>
      )}
    </div>
  );
}

export function EventMenuItems({ event }: { event: ApiEvent }) {
  const { t, getSection } = useTranslation();
  const lineItems = event.line_items?.filter((li) => li.item != null) ?? [];
  const spineItems = event.spine_order?.line_items ?? [];

  // No early return on an empty line-item list: per-pax events (buffets priced
  // by pax_count) carry no separately-priced line items, but still need their
  // food row and the delivery/surcharge/discount/totals footer.

  const eventTypes = getSection('events.event_types') as Record<string, string> | undefined;
  const units = getSection('events.units') as Record<string, string> | undefined;
  const eventTypeLabel = eventTypes?.[event.event_type] ?? toSentenceCase(event.event_type);
  const productLineLabel = event.product_line
    ? (PRODUCT_LINE_LABELS[event.product_line] ?? toSentenceCase(event.product_line))
    : null;
  const eventDescriptor = [eventTypeLabel, productLineLabel].filter(Boolean).join(INTERPUNCT);

  const pax = event.pax_count;
  const guestsLabel = units?.guests ?? 'guests';
  const quantityDisplay = pax != null ? `${pax} ${guestsLabel}` : null;
  // Match the quotation event row: per-pax unit price and food charge only.
  // Separately priced line items remain itemised in their own rows below.
  // A null price_per_pax means the event isn't priced per-pax (e.g. à la carte
  // charged via line items) — leave the event row's price and amount cells
  // blank rather than showing a misleading $0.00.
  const perPaxPrice = event.price_per_pax;
  const foodAmount = event.breakdown.food;

  const mobileTotals = [
    quantityDisplay && `${t('events_section.menu_items_columns.quantity')}: ${quantityDisplay}`,
    perPaxPrice != null && `${t('events_section.menu_items_columns.unit_price')}: ${formatCurrency(perPaxPrice)}`,
    perPaxPrice != null && `${t('events_section.menu_items_columns.amount')}: ${formatCurrency(foodAmount)}`,
  ].filter(Boolean).join(' | ');

  return (
    <div className="font-inter text-nav leading-normal text-off-black">
      <div
        role="table"
        aria-label={t('events_section.menu_items_columns.menu_items')}
        className="hidden md:block"
      >
        <div role="rowgroup" className="grid grid-cols-menu-items gap-x-6">
          <div role="row" className="contents">
            <MenuItemsHeader column="menu-items" className="pl-10">
              {t('events_section.menu_items_columns.menu_items')}
            </MenuItemsHeader>
            <MenuItemsHeader column="notes">
              {t('events_section.menu_items_columns.notes')}
            </MenuItemsHeader>
            <MenuItemsHeader column="quantity">
              {t('events_section.menu_items_columns.quantity')}
            </MenuItemsHeader>
            <MenuItemsHeader column="unit-price">
              {t('events_section.menu_items_columns.unit_price')}
            </MenuItemsHeader>
            <MenuItemsHeader column="amount">
              {t('events_section.menu_items_columns.amount')}
            </MenuItemsHeader>
          </div>
        </div>

        <div role="presentation" aria-hidden="true" className="border-t border-grey-light" />

        <div role="rowgroup" className="grid grid-cols-menu-items gap-x-6">
          <div role="row" className="contents">
            <div role="cell" data-column="menu-items" className="font-semibold py-6 pl-10">{eventDescriptor}</div>
            <div role="cell" data-column="notes" className="py-6" />
            <div role="cell" data-column="quantity" className="py-6">{quantityDisplay}</div>
            <div role="cell" data-column="unit-price" className="py-6">
              {perPaxPrice != null ? formatCurrency(perPaxPrice) : null}
            </div>
            <div role="cell" data-column="amount" className="py-6">
              {perPaxPrice != null ? formatCurrency(foodAmount) : null}
            </div>
          </div>
        </div>

        <div role="rowgroup" className="grid grid-cols-menu-items gap-x-6 gap-y-3">
          {lineItems.map((lineItem, index) => (
            <MenuItemRow key={lineItem.id} lineItem={lineItem} index={index} paxCount={pax} />
          ))}
          {spineItems.map((item, index) => (
            <SpineItemRow key={`spine-${index}`} item={item} index={lineItems.length + index} />
          ))}
        </div>
      </div>

      <div data-testid="menu-items-mobile" className="flex flex-col gap-6 md:hidden">
        <div className="flex flex-col gap-1.5">
          <p className="font-semibold">{eventDescriptor}</p>
          <p className="text-xs text-grey">{mobileTotals}</p>
        </div>
        <ol className="flex flex-col gap-3">
          {lineItems.map((lineItem, index) => (
            <MobileMenuItemRow key={lineItem.id} lineItem={lineItem} index={index} />
          ))}
          {spineItems.map((item, index) => (
            <MobileSpineItemRow key={`spine-${index}`} item={item} index={lineItems.length + index} />
          ))}
        </ol>
      </div>

      <EventBreakdownSummary breakdown={event.breakdown} bcrsDeposit={event.bcrs_deposit} />
    </div>
  );
}

function MenuItemsHeader({
  children,
  column,
  className,
}: {
  children: React.ReactNode;
  column: string;
  className?: string;
}) {
  return (
    <div
      role="columnheader"
      data-column={column}
      className={cn('font-inter text-sm font-semibold leading-normal text-grey py-1', className)}
    >
      {children}
    </div>
  );
}

// Menu (Product) rows note in dish_instructions_food/dish_instructions_ops; resale rows
// note in internal_notes/special_request (see FulfilmentEventSerializer#line_items_data).
function internalNotesFor(lineItem: ApiEventLineItem): string[] {
  return lineItem.item_type === 'ResaleItem'
    ? toNoteLines(lineItem.internal_notes, lineItem.special_request)
    : toNoteLines(lineItem.dish_instructions_food, lineItem.dish_instructions_ops);
}

function externalNotesFor(lineItem: ApiEventLineItem): string[] {
  return toNoteLines(lineItem.external_notes);
}

function MenuItemRow({
  lineItem,
  index,
  paxCount,
}: {
  lineItem: ApiEventLineItem;
  index: number;
  paxCount: number | null;
}) {
  const product = lineItem.item;
  const name = product?.name ?? '';
  // Included buffet dishes are descriptive; showing their stored $0 price is
  // misleading because their charge lives in the event's price_per_pax.
  const includedInPerPaxPrice = lineItem.quantity === paxCount && lineItem.unit_price === 0;
  const unavailability = lineItem.unavailability ?? null;

  return (
    <div role="row" className="contents">
      <div role="cell" data-column="menu-items">
        <div className="flex items-start gap-4 flex-nowrap">
          <span
            className={cn(
              'inline-flex items-center justify-center size-6 rounded-xs shrink-0',
              unavailability ? 'text-chilli-red' : 'text-off-black',
            )}
          >
            {index + 1}
          </span>
          <div className="min-w-0 font-normal">
            <NameWithDietIcons
              name={name}
              dietTags={product?.diet_tags}
              chefRecommended={product?.chef_recommended}
              className={unavailability ? 'text-chilli-red' : undefined}
            />
            {unavailability && (
              <p role="status" className="mt-1 text-sm font-inter text-chilli-red">
                {unavailability.message}
              </p>
            )}
          </div>
        </div>
      </div>
      <div role="cell" data-column="notes" className="min-w-0">
        <MenuItemNotes internalNotes={internalNotesFor(lineItem)} externalNotes={externalNotesFor(lineItem)} />
      </div>
      <div role="cell" data-column="quantity">
        {includedInPerPaxPrice ? null : lineItem.quantity}
      </div>
      <div role="cell" data-column="unit-price">
        {includedInPerPaxPrice ? null : formatCurrency(lineItem.unit_price)}
      </div>
      <div role="cell" data-column="amount">
        {includedInPerPaxPrice ? null : formatCurrency(lineItem.quantity * lineItem.unit_price)}
      </div>
    </div>
  );
}

// Linked Spine order line: same row shape as MenuItemRow, with a badge marking
// the item as sourced from Spine. Amounts are pre-GST, computed server-side.
function SpineItemRow({ item, index }: { item: ApiSpineOrderLineItem; index: number }) {
  const { t } = useTranslation();

  return (
    <div role="row" className="contents">
      <div role="cell" data-column="menu-items">
        <div className="flex items-start gap-4 flex-nowrap">
          <span className="inline-flex items-center justify-center size-6 rounded-xs text-off-black shrink-0">{index + 1}</span>
          <div className="flex min-w-0 flex-wrap items-center gap-2 font-normal">
            {/* A bundle whose template is gone has no name — keep the row identifiable. */}
            <span>{item.name ?? t('events_section.spine_item_unnamed')}</span>
            <Badge variant="tag">{t('events_section.spine_item_badge')}</Badge>
          </div>
        </div>
      </div>
      <div role="cell" data-column="notes" />
      <div role="cell" data-column="quantity">{item.quantity}</div>
      <div role="cell" data-column="unit-price">{formatCurrency(item.unit_price)}</div>
      <div role="cell" data-column="amount">{formatCurrency(item.amount)}</div>
    </div>
  );
}

function MobileSpineItemRow({ item, index }: { item: ApiSpineOrderLineItem; index: number }) {
  const { t } = useTranslation();

  return (
    <li className="flex gap-3">
      <span className="w-4 shrink-0">{index + 1}</span>
      <div className="flex min-w-0 flex-wrap items-center gap-2">
        <span className="font-normal">{item.name ?? t('events_section.spine_item_unnamed')}</span>
        <Badge variant="tag">{t('events_section.spine_item_badge')}</Badge>
      </div>
    </li>
  );
}

function MobileMenuItemRow({
  lineItem,
  index,
}: {
  lineItem: ApiEventLineItem;
  index: number;
}) {
  const product = lineItem.item;
  const name = product?.name ?? '';
  const unavailability = lineItem.unavailability ?? null;

  return (
    <li className="flex gap-3">
      <span className={cn('w-4 shrink-0', unavailability && 'text-chilli-red')}>{index + 1}</span>
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className={cn('font-normal', unavailability && 'text-chilli-red')}>{name}</span>
          {product ? (
            <div className="flex items-center gap-1">
              <DietTagIcons tags={product.diet_tags} chefRecommended={product.chef_recommended} />
            </div>
          ) : null}
        </div>
        {unavailability && (
          <p role="status" className="text-sm font-inter text-chilli-red">
            {unavailability.message}
          </p>
        )}
        <MenuItemNotes internalNotes={internalNotesFor(lineItem)} externalNotes={externalNotesFor(lineItem)} />
      </div>
    </li>
  );
}

// Collapsed-accordion header summary: a right-aligned preview of the section's
// content, hidden once the section expands (relies on `group` on the trigger).
export function SectionSummary({ children }: { children: React.ReactNode }) {
  return (
    <span className="ml-auto max-w-[55%] truncate pr-2 font-inter text-sm font-normal text-grey group-data-[state=open]:hidden">
      {children}
    </span>
  );
}

function Label({ children }: { children: React.ReactNode }) {
  return (
    <p className="font-inter text-sm font-semibold leading-normal text-grey">{children}</p>
  );
}

function Value({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <p className={cn('font-inter text-sm font-normal leading-normal text-off-black', className)}>{children}</p>
  );
}

function EmptyValue() {
  return (
    <p className="mt-1.5 font-inter text-sm font-normal leading-normal text-off-black">&mdash;</p>
  );
}

function TimingColumn({ label, value, className }: { label: string; value: string | null | undefined; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label>{label}</Label>
      {value ? <Value>{formatTime(value)}</Value> : <EmptyValue />}
    </div>
  );
}
